import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Link as MuiLink } from '@mui/material';
import { MAIN_BG_COLOR } from '../theme';
import { PRODUCT_NAV_ITEMS } from '../pages/productPages';
import { CLIENT_TYPE_NAV_ITEMS } from '../pages/clientPages';

const FOOTER = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    dark: MAIN_BG_COLOR,
    borderLight: '0.5px solid rgba(255,255,255,0.12)',
    sectionPaddingX: '1.25rem',
} as const;

const columnLabelSx = {
    fontFamily: FOOTER.fontBody,
    fontWeight: 400,
    fontSize: '0.6875rem',
    color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    mb: 1.5,
};

const footerLinkSx = {
    fontFamily: FOOTER.fontBody,
    fontWeight: 400,
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.72)',
    textDecoration: 'none',
    lineHeight: 1.45,
    '&:hover': { color: '#fff' },
} as const;

const RECURSOS = [
    {
        label: 'Newsletter',
        href: 'https://radar.podscope.co',
        external: true,
    },
    {
        label: 'Ranking semanal',
        to: '/ranking',
    },
    {
        label: 'Livestreams en vivo',
        to: '/live',
    },
] as const;

const Footer: React.FC = () => {
    return (
        <Box
            component="footer"
            sx={{
                width: '100%',
                background: FOOTER.dark,
                borderTop: FOOTER.borderLight,
                py: { xs: 5, md: 6 },
                mt: 'auto',
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: FOOTER.sectionPaddingX, md: 4 },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: '1fr 1fr',
                            md: '1.2fr 1fr 1fr 0.9fr 0.95fr',
                        },
                        gap: { xs: 4, md: 3.5 },
                        alignItems: 'start',
                    }}
                >
                    <Box>
                        <Typography
                            component={Link}
                            to="/"
                            sx={{
                                fontFamily: FOOTER.fontHeading,
                                fontWeight: 800,
                                fontSize: '1.25rem',
                                color: '#fff',
                                textDecoration: 'none',
                                display: 'inline-block',
                                mb: 1,
                            }}
                        >
                            Podscope
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FOOTER.fontBody,
                                fontWeight: 400,
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                color: 'rgba(255,255,255,0.55)',
                                maxWidth: 260,
                            }}
                        >
                            Inteligencia y verificación de medios digitales.
                        </Typography>
                    </Box>

                    <Box>
                        <Typography sx={columnLabelSx}>Por caso de uso</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {PRODUCT_NAV_ITEMS.map(item => (
                                <MuiLink
                                    key={item.to}
                                    component={Link}
                                    to={item.to}
                                    sx={footerLinkSx}
                                >
                                    {item.label}
                                </MuiLink>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={columnLabelSx}>Por tipo de cliente</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {CLIENT_TYPE_NAV_ITEMS.map(item => (
                                <MuiLink
                                    key={item.to}
                                    component={Link}
                                    to={item.to}
                                    sx={footerLinkSx}
                                >
                                    {item.label}
                                </MuiLink>
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={columnLabelSx}>Recursos</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {RECURSOS.map(item =>
                                'external' in item && item.external ? (
                                    <MuiLink
                                        key={item.label}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={footerLinkSx}
                                    >
                                        {item.label}
                                    </MuiLink>
                                ) : (
                                    <MuiLink
                                        key={item.label}
                                        component={Link}
                                        to={'to' in item ? item.to : '/'}
                                        sx={footerLinkSx}
                                    >
                                        {item.label}
                                    </MuiLink>
                                )
                            )}
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={columnLabelSx}>Legal</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                            <MuiLink component={Link} to="/legal/privacidad" sx={footerLinkSx}>
                                Política de Privacidad
                            </MuiLink>
                            <MuiLink component={Link} to="/legal/terminos" sx={footerLinkSx}>
                                Términos y Condiciones
                            </MuiLink>
                        </Box>
                        <Typography sx={columnLabelSx}>Contacto</Typography>
                        <MuiLink href="mailto:esteban@podscope.co" sx={footerLinkSx}>
                            esteban@podscope.co
                        </MuiLink>
                        <Typography
                            sx={{
                                fontFamily: FOOTER.fontBody,
                                fontWeight: 400,
                                fontSize: '0.875rem',
                                lineHeight: 1.5,
                                color: 'rgba(255,255,255,0.55)',
                                mt: 2,
                            }}
                        >
                            © 2026 CONTENTSCOPE SpA
                        </Typography>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default Footer;
